import React, { useContext, useEffect } from "react";
import { Dialog, DialogContent, Grid, Link, Typography } from "@mui/material";
import { LocalizeText } from "components/localizer";
import SmallBanner from "components/smallBanner";
import { ProfileContext } from "contexts/profile";
import { useNavigate } from "react-router-dom";
import If from "components/__helpers__/if";
import NewsSidebar from "components/newsComponents/NewsSidebar";
import { usePermission } from "utils/hooks/usePermission";
import colors from "styles/colors";
import VKButton from "components/vkButton";
import ArrowRightIcon from "@mui/icons-material/NavigateNext";
import { permission } from "utils/permissions";
import { TLanguageTag } from "contexts/language";
import { exhaustive } from "exhaustive";
import SubscriptionsContext from "contexts/subscriptions";
import { MembershipsContext } from "contexts/memberships";

type AvailableDialogs = "Tenant" | "ArenaMember";
type ActionButtonTag = "APPLY" | "CANCEL" | "INVOICE" | "MANAGE_USERS";
type ActionButtonStates = undefined | "LOADING" | ActionButtonTag;

const StartpageView: React.FC = () => {
    const { fetchMemberships, pagination } = useContext(MembershipsContext);
    const { user, landingPage, selectedOrganization } = React.useContext(ProfileContext);
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const [selectedDialog, setDialog] = React.useState<AvailableDialogs | null>(null);
    const [actionButtonIsLoading, setActionButtonIsLoading] = React.useState<ActionButtonStates>(undefined);
    const { getBariumRequestMoreMembershipURL } = useContext(SubscriptionsContext);
    const handleClose = (): void => {
        setDialog(null);
    };

    useEffect(() => {
        pagination.initialize(fetchMemberships);
        pagination.setParam("ordering", "organization__name");
        pagination.setParam("search", selectedOrganization.org_nr);
        const isSingleMembership = async (): Promise<void> => {
            pagination.getInitial().finally(() => setActionButtonIsLoading(undefined));
        };
        isSingleMembership();
        // eslint-disable-next-line
    }, []);

    const changeLandingPage = (): void => {
        window.localStorage.setItem("landingPage", landingPage === "community" ? "default" : "community");
        window.location.reload();
    };

    const renderDialog = (): JSX.Element => {
        return (
            <Dialog open={!!selectedDialog} onClose={handleClose} maxWidth="md">
                <If truthy={selectedDialog}>
                    <DialogContent sx={{ padding: "3rem" }}>
                        <Typography variant="h4">
                            <LocalizeText tag={`dialogTitle${selectedDialog}` as TLanguageTag} />
                        </Typography>
                        <br />
                        <Typography variant="body1">
                            <LocalizeText tag={`teaser${selectedDialog}` as TLanguageTag} />
                        </Typography>
                        <br />
                        <If truthy={selectedDialog === "Tenant"}>
                            <Link
                                variant="body1"
                                href="https://vasakronan.se/lediga-lokaler/"
                                target="_blank"
                                underline="hover"
                            >
                                <LocalizeText tag="availablePremises" />
                            </Link>
                        </If>
                        <If truthy={selectedDialog === "ArenaMember"}>
                            <Link
                                variant="body1"
                                href="https://vasakronan.se/arena-coworking/"
                                target="_blank"
                                underline="hover"
                            >
                                <LocalizeText tag="arenaCoworking" />
                            </Link>
                        </If>
                    </DialogContent>
                </If>
            </Dialog>
        );
    };

    const shouldShowArenaMemberInformation = hasPermissions(permission.ARENA_DASHBOARD);
    const shouldShowTenantInformation = hasPermissions(permission.TENANT_DASHBOARD);

    const navigationShortcut = (textTag: React.ReactNode, buttonTag: ActionButtonTag, onClick: () => void, loading: ActionButtonStates): React.ReactNode => {
        return (<VKButton
            size="small"
            style={{ marginBottom: "1rem", display: "flex", width: "80%" }}
            template="primary"
            rightAddon={true} // For some reason if there is an addon then the correct styling is added
            disabled={!shouldShowArenaMemberInformation || loading !== undefined}
            onClick={onClick}
            isLoading={buttonTag === loading}>
            <Typography variant="body1" textAlign={"left"}>
                {textTag}
            </Typography>
            <ArrowRightIcon sx={{ fontSize: "42px", display: "flex" }} />
        </VKButton>);
    };

    const handleActionBasedOnMembership = (state: ActionButtonTag): void => {
        setActionButtonIsLoading(state);
        const maybeMembershipId = pagination.totalCount === 1 ? Number(pagination.results[0].id) : null;
        exhaustive(state, {
            MANAGE_USERS: () => {
                if (maybeMembershipId) {
                    navigate(`/members/${maybeMembershipId}`);
                } else {
                    navigate("/members");
                }
            },
            APPLY: () => {
                if (maybeMembershipId) {
                    getBariumRequestMoreMembershipURL(maybeMembershipId).finally(() => setActionButtonIsLoading(undefined));
                } else {
                    navigate("/subscriptions?activity=apply");
                }
            },
            CANCEL: () => {
                if (maybeMembershipId) {
                    navigate(`/subscriptions/${maybeMembershipId}`);
                } else {
                    navigate("/subscriptions");
                }
            },
            INVOICE: () => {
                if (maybeMembershipId) {
                    navigate(`/staff-memberships/${maybeMembershipId}/details`);
                } else {
                    navigate("/memberships");
                }
            }
        });
    };

    return (
        <Grid
            data-testid="StartpageView-root"
            container
            item
            wrap="nowrap"
            sx={{
                minHeight: "100%",
                backgroundColor: colors.white,
            }}
        >
            <Grid item md={12} lg={12} sx={{ padding: "40px 60px" }}>
                <Grid container item lg={12} justifyContent="space-evenly">
                    <Grid item lg={10} md={12}>
                        <Typography variant="h3" gutterBottom style={{ fontFamily: "Vasakronan" }}>
                            <LocalizeText
                                tag="dashboardTitle"
                                replace={user && { firstName: user.first_name, lastName: user.last_name }}
                            />
                        </Typography>
                    </Grid>
                    <Grid item lg={10} md={12}>
                        {landingPage === "community" ? (
                            <Typography variant="body1" style={{ fontFamily: "Apercu" }}>
                                <LocalizeText tag="dashboardBreadcrumbsStaff" />
                            </Typography>
                        ) : (
                            <Typography variant="body1" style={{ fontFamily: "Apercu" }}>
                                <LocalizeText tag="dashboardBreadcrumbs" />
                                <Link href="https://www.vasakronan.se/gdpr" target="_blank" rel="noopener noreferrer">
                                www.vasakronan.se/gdpr
                                </Link>
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                {landingPage === "community" ? (
                    <Grid container item md={12} lg={12} justifyContent="space-evenly">
                        <Grid
                            item
                            lg={5}
                            md={12}
                            sx={{ paddingTop: "2rem" }}
                            onClick={() => navigate("/staff-memberships")}
                        >
                            <SmallBanner
                                title="communityStaff"
                                image={`${process.env.PUBLIC_URL}/assets/images/arenaStaff/startPageArenaStaff.jpg`}
                            />
                        </Grid>
                        <Grid item lg={5} md={12} sx={{ paddingTop: "2rem" }} onClick={changeLandingPage}>
                            <SmallBanner
                                title="showAsRepresentative"
                                image={`${process.env.PUBLIC_URL}/assets/images/arenaStaff/startPageKundportalenRepresentative.jpg`}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container item md={12} lg={12} gridTemplateColumns={"auto auto"} columnGap={"3rem"} justifyContent={"center"}>
                        <Grid
                            item
                            lg={5}
                            md={12}
                            sx={{ paddingTop: "2rem" }}
                            onClick={() => {
                                !shouldShowArenaMemberInformation
                                    ? setDialog("ArenaMember")
                                    : navigate("/arena-member-dashboard");
                            }}
                        >
                            <SmallBanner
                                inactive={!shouldShowArenaMemberInformation}
                                title="arenaMember"
                                image={`${process.env.PUBLIC_URL}/assets/images/representatives/startPageArenaMember.jpg`}
                            />
                        </Grid>
                        <Grid
                            item
                            lg={5}
                            md={12}
                            sx={{ paddingTop: "2rem", width: "100%" }}
                            onClick={() => {
                                if (!shouldShowArenaMemberInformation) {
                                    setDialog("ArenaMember");
                                }
                            }}>
                            {navigationShortcut(<LocalizeText tag="manageUsers" />, "MANAGE_USERS", () => handleActionBasedOnMembership("MANAGE_USERS"), actionButtonIsLoading)}
                            {navigationShortcut(<LocalizeText tag="applyForMoreSubscriptions" />, "APPLY", () => handleActionBasedOnMembership("APPLY"), actionButtonIsLoading)}
                            {navigationShortcut(<LocalizeText tag="cancelSubscription" />, "CANCEL", () => handleActionBasedOnMembership("CANCEL"), actionButtonIsLoading)}
                            {navigationShortcut(<LocalizeText tag="editInvoiceDetails" />, "INVOICE", () => handleActionBasedOnMembership("INVOICE"), actionButtonIsLoading)}
                        </Grid>
                        <Grid
                            item
                            lg={5}
                            md={12}
                            sx={{ paddingTop: "2rem" }}
                            onClick={() => {
                                !shouldShowTenantInformation
                                    ? setDialog("Tenant")
                                    : navigate("/tenant-dashboard");
                            }}
                        >
                            <SmallBanner
                                inactive={!shouldShowTenantInformation}
                                title="tenantCategory"
                                image={`${process.env.PUBLIC_URL}/assets/images/representatives/startPageTenant.jpg`}
                            />
                        </Grid>
                        <Grid //Empty cell in grid to align items
                            item
                            lg={5}
                            md={12}
                            sx={{ paddingTop: "2rem" }}
                        />
                    </Grid >
                )}
            </Grid >
            {shouldShowTenantInformation ? <NewsSidebar /> : null}

            {renderDialog()}
        </Grid >
    );
};

export default StartpageView;
